import React, { useState } from 'react';
import { FaPlus, FaPenToSquare, FaTrash, FaBook, FaDiamond, FaXmark, FaGraduationCap } from 'react-icons/fa6';
import { useChapters } from '../../hooks/useChapters';
import { useAuth } from '../../hooks/useAuth';
import AppIcon from '../ui/AppIcon';

const initialFormData = {
  title: '',
  description: '',
  content: '',
  advancedContent: '',
  fullContent: '',
  exercises: [],
  requiresAuth: false,
  hasExercises: false,
  contentTier: 'basic',
  order: 1,
};

const initialExercise = {
  question: '',
  type: 'multiple-choice',
  options: ['', '', '', ''],
  answer: '',
  explanation: '',
};

const AdminPage = () => {
  const { chapters, createChapter, updateChapter, deleteChapter, loading } = useChapters();
  const { user } = useAuth();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [currentExercise, setCurrentExercise] = useState(initialExercise);
  const [saving, setSaving] = useState(false);

  const isAdmin = user && user.email === 'admin@escape.com';

  if (!isAdmin) {
    return (
      <div className="container" style={{ padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 80, color: 'var(--danger-color)', marginBottom: 16 }}>🛡️</div>
        <h2 style={{ color: 'var(--danger-color)', marginBottom: 8 }}>Access Denied</h2>
        <p>You don't have permission to access the admin panel.</p>
      </div>
    );
  }

  const resetForm = () => {
    setFormData(initialFormData);
    setCurrentExercise(initialExercise);
    setEditingChapter(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalVisible(true);
  };

  const openEditModal = (chapter) => {
    setFormData({
      title: chapter.title,
      description: chapter.description,
      content: chapter.content || '',
      advancedContent: chapter.advancedContent || '',
      fullContent: chapter.fullContent || '',
      exercises: chapter.exercises || [],
      requiresAuth: chapter.requiresAuth || false,
      hasExercises: chapter.hasExercises || false,
      contentTier: chapter.contentTier || 'basic',
      order: chapter.order || 1,
    });
    setEditingChapter(chapter);
    setModalVisible(true);
  };

  const addExercise = () => {
    if (!currentExercise.question.trim() || !currentExercise.answer.trim()) {
      alert('Question and answer are required for exercises.');
      return;
    }

    const newExercise = {
      id: Date.now().toString(),
      ...currentExercise,
      options: currentExercise.type === 'multiple-choice'
        ? currentExercise.options.filter(opt => opt.trim())
        : undefined,
    };

    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, newExercise],
      hasExercises: true,
    }));

    setCurrentExercise(initialExercise);
  };

  const removeExercise = (exerciseId) => {
    setFormData(prev => {
      const remaining = prev.exercises.filter(ex => ex.id !== exerciseId);
      return { ...prev, exercises: remaining, hasExercises: remaining.length > 0 };
    });
  };

  const updateExerciseOption = (index, value) => {
    const newOptions = [...currentExercise.options];
    newOptions[index] = value;
    setCurrentExercise(prev => ({ ...prev, options: newOptions }));
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      alert('Title and description are required.');
      return;
    }

    setSaving(true);
    const chapterData = {
      ...formData,
      order: parseInt(formData.order) || 1,
    };

    try {
      let result;
      if (editingChapter) {
        result = await updateChapter(editingChapter.id, chapterData);
      } else {
        result = await createChapter(chapterData);
      }

      if (result && result.success) {
        setModalVisible(false);
        resetForm();
      } else {
        alert(result?.error || 'Failed to save experience.');
      }
    } catch (error) {
      alert(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (chapter) => {
    if (!window.confirm(`Are you sure you want to delete "${chapter.title}"?`)) return;
    deleteChapter(chapter.id).then(result => {
      if (!result.success) alert(result.error);
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <AppIcon size={40} />
          <h2 style={{ margin: 0 }}>Admin Panel</h2>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <FaPlus /> Add Experience
        </button>
      </div>

      {/* Chapter List */}
      <div className="container" style={{ padding: '24px 20px' }}>
        {loading && (
          <div className="loading">
            <div className="spinner" />
            Loading experiences...
          </div>
        )}

        {!loading && chapters.length === 0 && (
          <div className="text-center" style={{ padding: 60, color: 'var(--gray-color)' }}>
            <FaBook size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
            <h3>No Experiences Yet</h3>
            <p>Click "Add Experience" to create your first one.</p>
          </div>
        )}

        {chapters.map((chapter) => (
          <div className="card" key={chapter.id} style={{ cursor: 'default' }}>
            <div style={styles.cardHeader}>
              <h4 style={{ margin: 0, flex: 1 }}>{chapter.title}</h4>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn"
                  style={styles.iconBtn}
                  onClick={() => openEditModal(chapter)}
                  title="Edit"
                >
                  <FaPenToSquare color="var(--primary-color)" />
                </button>
                <button
                  className="btn"
                  style={styles.iconBtnDanger}
                  onClick={() => handleDelete(chapter)}
                  title="Delete"
                >
                  <FaTrash color="var(--danger-color)" />
                </button>
              </div>
            </div>
            <p style={{ marginBottom: 12, color: 'var(--gray-color)', fontSize: 14 }}>
              {chapter.description}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{
                ...styles.badge,
                background: chapter.contentTier === 'advanced' ? '#fff3cd' : '#e7f1ff',
                color: chapter.contentTier === 'advanced' ? '#856404' : '#0d6efd',
              }}>
                {chapter.contentTier === 'advanced'
                  ? <><FaDiamond size={10} /> Advanced</>
                  : <><FaBook size={10} /> Basic</>
                }
              </span>
              <span style={{ ...styles.badge, background: '#e9ecef', color: '#6c757d' }}>
                Order: {chapter.order || '-'}
              </span>
              {chapter.requiresAuth && (
                <span style={{ ...styles.badge, background: '#e9ecef', color: '#6c757d' }}>
                  🔒 Auth Required
                </span>
              )}
              {chapter.exercises && chapter.exercises.length > 0 && (
                <span style={{ ...styles.badge, background: '#d1e7dd', color: '#0f5132' }}>
                  <FaGraduationCap size={10} /> {chapter.exercises.length} Questions
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalVisible && (
        <div style={styles.overlay} onClick={() => setModalVisible(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>
                {editingChapter ? 'Edit Experience' : 'Create Experience'}
              </h3>
              <button
                className="btn"
                style={styles.closeBtn}
                onClick={() => setModalVisible(false)}
              >
                <FaXmark size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input
                  className="form-input"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter experience title"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Order</label>
                <input
                  className="form-input"
                  type="number"
                  value={formData.order}
                  onChange={e => setFormData({ ...formData, order: e.target.value })}
                  placeholder="Chapter order (e.g., 1)"
                  min="1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description *</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter experience description"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Content (Overview)</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={formData.content}
                  onChange={e => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter basic experience content (requires login)"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Detailed Content (Advanced)</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={formData.advancedContent}
                  onChange={e => setFormData({ ...formData, advancedContent: e.target.value })}
                  placeholder="Enter advanced content (requires login)"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Full Content</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={formData.fullContent}
                  onChange={e => setFormData({ ...formData, fullContent: e.target.value })}
                  placeholder="Full detailed content (for subscribers)"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Content Tier</label>
                <div style={styles.tierInfo}>
                  Basic: Viewable by everyone &bull; Advanced: Login required
                </div>
                <select
                  className="form-input"
                  value={formData.contentTier}
                  onChange={e => setFormData({ ...formData, contentTier: e.target.value })}
                >
                  <option value="basic">📖 Basic (Everyone)</option>
                  <option value="advanced">💎 Advanced (Login Required)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={formData.requiresAuth}
                    onChange={e => setFormData({ ...formData, requiresAuth: e.target.checked })}
                  />
                  Requires Authentication
                </label>
              </div>

              {/* Exercises Section */}
              <div className="form-group">
                <label className="form-label">Exercises</label>

                {formData.exercises.map((exercise, index) => (
                  <div key={exercise.id} style={styles.exerciseItem}>
                    <span style={{ flex: 1, fontSize: 14, color: '#495057' }}>
                      {index + 1}. {exercise.question}
                    </span>
                    <button
                      className="btn"
                      style={styles.iconBtnSmall}
                      onClick={() => removeExercise(exercise.id)}
                    >
                      <FaTrash size={12} color="var(--danger-color)" />
                    </button>
                  </div>
                ))}

                <div style={styles.exerciseForm}>
                  <h5 style={{ margin: '0 0 12px', color: '#495057' }}>Add New Exercise</h5>

                  <div className="form-group">
                    <label className="form-label">Question</label>
                    <input
                      className="form-input"
                      value={currentExercise.question}
                      onChange={e => setCurrentExercise({ ...currentExercise, question: e.target.value })}
                      placeholder="Exercise question"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Exercise Type</label>
                    <select
                      className="form-input"
                      value={currentExercise.type}
                      onChange={e => setCurrentExercise({ ...currentExercise, type: e.target.value })}
                    >
                      <option value="multiple-choice">Multiple Choice</option>
                      <option value="text">Text Answer</option>
                      <option value="code">Code</option>
                    </select>
                  </div>

                  {currentExercise.type === 'multiple-choice' && (
                    <div className="form-group">
                      <label className="form-label">Options</label>
                      {currentExercise.options.map((option, index) => (
                        <input
                          key={index}
                          className="form-input"
                          style={{ marginBottom: 8 }}
                          value={option}
                          onChange={e => updateExerciseOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Correct Answer</label>
                    <input
                      className="form-input"
                      value={currentExercise.answer}
                      onChange={e => setCurrentExercise({ ...currentExercise, answer: e.target.value })}
                      placeholder="Correct answer"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Explanation (Optional)</label>
                    <textarea
                      className="form-input"
                      rows={2}
                      value={currentExercise.explanation}
                      onChange={e => setCurrentExercise({ ...currentExercise, explanation: e.target.value })}
                      placeholder="Answer explanation"
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  <button
                    className="btn btn-secondary"
                    style={{ width: '100%' }}
                    onClick={addExercise}
                  >
                    <FaPlus size={14} /> Add Exercise
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={styles.modalFooter}>
              <button
                className="btn btn-outline"
                onClick={() => setModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : editingChapter ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    background: '#fff',
    borderBottom: '1px solid #e9ecef',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  iconBtn: {
    padding: 8,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    minWidth: 'auto',
  },
  iconBtnDanger: {
    padding: 8,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    minWidth: 'auto',
  },
  iconBtnSmall: {
    padding: 4,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    minWidth: 'auto',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: 40,
    zIndex: 1000,
    overflowY: 'auto',
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 640,
    margin: '0 16px 40px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 80px)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e9ecef',
    flexShrink: 0,
  },
  closeBtn: {
    padding: 4,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    color: '#6c757d',
    minWidth: 'auto',
  },
  modalBody: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1,
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '16px 24px',
    borderTop: '1px solid #e9ecef',
    flexShrink: 0,
  },
  tierInfo: {
    background: '#f8f9fa',
    padding: 10,
    borderRadius: 6,
    marginBottom: 8,
    fontSize: 12,
    color: '#6c757d',
    textAlign: 'center',
    border: '1px solid #e9ecef',
  },
  exerciseItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f8f9fa',
    padding: 12,
    borderRadius: 6,
    marginBottom: 8,
  },
  exerciseForm: {
    background: '#f8f9fa',
    padding: 16,
    borderRadius: 8,
    marginTop: 10,
    border: '1px solid #dee2e6',
  },
};

export default AdminPage;